import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const BankModal = ({ 
  open, 
  onClose, 
  onSave, 
  bankData = null, 
  mode = 'create', // 'create', 'edit', 'view'
  balanceSheetHeads = [],
  existingBanks = [] // Add this prop to receive the list of existing banks
}) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  
  const [formData, setFormData] = useState({
    BalanceSheetHeadId: '',
    Name: '',
    AccountNumber: '',
    IFSCCode: '',
    BranchName: '',
    BranchAddress: '',
    ContactNumber: '',
    PaymentQRcodeUPIID: '',
    UPIName: '',
    IsPrimary: false
  });

  const [errors, setErrors] = useState({});
  const [primaryWarningDialogOpen, setPrimaryWarningDialogOpen] = useState(false);
  const [primaryWarningMessage, setPrimaryWarningMessage] = useState('');

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      console.log('Modal opened with mode:', mode, 'bankData:', bankData);
      
      if (bankData && mode !== 'create') {
        console.log('Loading existing bank data for edit/view, ID:', bankData.Id);
        setFormData({
          Id: bankData.Id || '',
          BalanceSheetHeadId: bankData.BalanceSheetHeadId || '',
          Name: bankData.Name || '',
          AccountNumber: bankData.AccountNumber || '',
          IFSCCode: bankData.IFSCCode || '',
          BranchName: bankData.BranchName || '',
          BranchAddress: bankData.BranchAddress || '',
          ContactNumber: bankData.ContactNumber || '',
          PaymentQRcodeUPIID: bankData.PaymentQRcodeUPIID || '',
          UPIName: bankData.UPIName || '',
          IsPrimary: bankData.IsPrimary || false
        });
      } else {
        console.log('Creating new bank');
        // Reset form for create mode
        setFormData({
          BalanceSheetHeadId: '',
          Name: '',
          AccountNumber: '',
          IFSCCode: '',
          BranchName: '',
          BranchAddress: '',
          ContactNumber: '',
          PaymentQRcodeUPIID: '',
          UPIName: '',
          IsPrimary: false
        });
      }
      setErrors({});
    }
  }, [open, bankData, mode]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Validate Account Number - only allow digits
    if (name === 'AccountNumber' && value) {
      const accountNumberPattern = /^[0-9]*$/;
      if (!accountNumberPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    // Validate IFSC Code - only allow uppercase letters and digits
    if (name === 'IFSCCode') {
      const upperValue = value.toUpperCase();
      const ifscPattern = /^[A-Z0-9]*$/;
      if (upperValue && !ifscPattern.test(upperValue)) {
        return; // Don't update if invalid characters
      }
      setFormData(prev => ({ ...prev, IFSCCode: upperValue }));
      if (errors['IFSCCode']) setErrors(prev => ({ ...prev, IFSCCode: '' }));
      return;
    }
    
    // Validate Contact Number - only allow digits, spaces, +, -, and ()
    if (name === 'ContactNumber' && value) {
      const numericPattern = /^[0-9+\-\s()]*$/;
      if (!numericPattern.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.Name.trim()) {
      newErrors.Name = 'Bank name is required';
    }
    
    if (!formData.BalanceSheetHeadId) {
      newErrors.BalanceSheetHeadId = 'Balance Sheet Head is required';
    }
    
    if (!formData.AccountNumber.trim()) {
      newErrors.AccountNumber = 'Account number is required';
    }

    if (!formData.IFSCCode.trim()) {
      newErrors.IFSCCode = 'IFSC code is required';
    } else {
      // Validate IFSC Code format (11 characters: 4 letters + 0 + 6 alphanumeric)
      const ifscPattern = /^[A-Z]{4}0[A-Z0-9]{6}$/;
      if (!ifscPattern.test(formData.IFSCCode)) {
        newErrors.IFSCCode = 'Invalid IFSC format. Must be 11 characters (e.g., SBIN0001234)';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkPrimaryBank = () => {
    // Only check if user is trying to set this bank as primary
    if (!formData.IsPrimary) {
      return null;
    }

    // Check if another bank already has primary status
    const existingPrimaryBank = existingBanks.find(bank => {
      // Skip the current bank in edit mode
      if (mode === 'edit' && bankData && bank.Id === bankData.Id) {
        return false;
      }
      
      // Check if another bank is marked as primary
      return bank.IsPrimary === true;
    });
    
    return existingPrimaryBank;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    // Check for existing primary bank
    const existingPrimaryBank = checkPrimaryBank();
    if (existingPrimaryBank) {
      setPrimaryWarningMessage(`Another bank "${existingPrimaryBank.Name}" is already marked as primary. Only one bank can be set as primary at a time.`);
      setPrimaryWarningDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        BalanceSheetHeadId: parseInt(formData.BalanceSheetHeadId) || 0,
        Name: formData.Name,
        AccountNumber: formData.AccountNumber,
        IFSCCode: formData.IFSCCode,
        BranchName: formData.BranchName,
        BranchAddress: formData.BranchAddress,
        ContactNumber: formData.ContactNumber,
        PaymentQRcodeUPIID: formData.PaymentQRcodeUPIID,
        UPIName: formData.UPIName,
        IsPrimary: formData.IsPrimary,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        // Validate that we have the bank ID
        if (!bankData || !bankData.Id) {
          console.error('Cannot update: Missing bank ID');
          alert('Error: Cannot update bank - missing ID');
          return;
        }
        dataToSend.Id = bankData.Id;
        // For edit mode, add modification fields but don't include ID in body (it's in URL)
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        // Keep original creation data if available
        if (bankData.CreatedBy) dataToSend.CreatedBy = bankData.CreatedBy;
        if (bankData.CreatedDate) dataToSend.CreatedDate = bankData.CreatedDate;
        
        console.log('Updating bank with ID:', bankData.Id);
        console.log('Update data being sent:', dataToSend);
        console.log('API call: PUT /BankApi/' + bankData.Id);
        
        const response = await updateData('BankApi', bankData.Id, dataToSend, token);
        console.log('Update response:', response);
      } else {
        // For create mode, add creation fields
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();
        
        console.log('Creating new bank');
        console.log('Create data being sent:', dataToSend);
        console.log('API call: POST /BankApi');
        
        const response = await postData('BankApi', dataToSend, token);
        console.log('Create response:', response);
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving bank:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Bank';
      case 'edit': return 'Edit Bank';
      case 'view': return 'Bank Details';
      default: return 'Bank';
    }
  };

  const isReadOnly = mode === 'view';

  // Common input styles
  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '8px'
        }
      }}
    >
      <Box style={{
        background: 'white',
        borderRadius: isMobile ? '0' : '10px',
        overflow: 'hidden',
        height: isMobile ? '100vh' : 'auto'
      }}>
        <DialogTitle style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 'auto'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AccountBalanceIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
              {getTitle()}
            </Typography>
          </Box>
          <IconButton 
            onClick={onClose}
            style={{ color: 'white', padding: '2px' }}
            size="small"
          >
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ 
          padding: '8px 12px', 
          paddingBottom: '4px'
        }}>
          <Typography variant="subtitle2" style={{ 
            color: '#666', 
            marginBottom: '6px',
            fontSize: '10px',
            lineHeight: '1.2'
          }}>
            {mode === 'create' ? 'Add a new bank to your system' : 
             mode === 'edit' ? 'Update bank information' : 
             'View bank details'}
          </Typography>

          <Grid container spacing={1}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ 
                fontWeight: '600', 
                marginBottom: '3px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Basic Information
              </Typography>
            </Grid>

            {/* Bank Name and Balance Sheet Head - Same Row */}
            <Grid item xs={12} sm={8}>
              <TextField
                name="Name"
                label="Bank Name *"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.Name}
                helperText={errors.Name}
                disabled={isReadOnly}
                placeholder="Enter bank name"
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                select
                name="BalanceSheetHeadId"
                label="Balance Sheet Head *"
                value={formData.BalanceSheetHeadId}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.BalanceSheetHeadId}
                helperText={errors.BalanceSheetHeadId}
                disabled={isReadOnly}
                SelectProps={{ native: true }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              >
                <option value=""></option>
                {balanceSheetHeads.map(head => (
                  <option key={head.Id} value={head.Id}>{head.Name}</option>
                ))}
              </TextField>
            </Grid>

            {/* Account Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ 
                fontWeight: '600', 
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Account Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="AccountNumber"
                label="Account Number *"
                value={formData.AccountNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.AccountNumber}
                helperText={errors.AccountNumber}
                disabled={isReadOnly}
                placeholder="Enter account number"
                inputProps={{ maxLength: 20 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="IFSCCode"
                label="IFSC Code *"
                value={formData.IFSCCode}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.IFSCCode}
                helperText={errors.IFSCCode}
                disabled={isReadOnly}
                placeholder="Enter IFSC code"
                inputProps={{ maxLength: 11 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
                FormHelperTextProps={{ style: helperStyle }}
              />
            </Grid>

            {/* Branch Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ 
                fontWeight: '600', 
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                Branch Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="BranchName"
                label="Branch Name"
                value={formData.BranchName}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter branch name"
                inputProps={{ maxLength: 100 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="ContactNumber"
                label="Contact Number"
                value={formData.ContactNumber}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="+91 XXXXX XXXXX"
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                name="BranchAddress"
                label="Branch Address"
                value={formData.BranchAddress}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter branch address"
                multiline
                rows={2}
                inputProps={{ maxLength: 200 }}
                InputProps={{ style: { borderRadius: '6px', fontSize: '13px' } }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* UPI Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" style={{ 
                fontWeight: '600', 
                marginBottom: '3px',
                marginTop: '2px',
                color: '#2d3748',
                fontSize: '12px'
              }}>
                UPI Details
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="PaymentQRcodeUPIID"
                label="UPI ID"
                value={formData.PaymentQRcodeUPIID}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="example@upi"
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                name="UPIName"
                label="UPI Name"
                value={formData.UPIName}
                onChange={handleChange}
                fullWidth
                size="small"
                disabled={isReadOnly}
                placeholder="Enter UPI name"
                inputProps={{ maxLength: 50 }}
                InputProps={{ style: inputStyle }}
                InputLabelProps={{ style: labelStyle }}
              />
            </Grid>

            {/* Primary Bank Setting */}
            <Grid item xs={12}>
              <Box style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 8px',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                backgroundColor: isReadOnly ? '#f5f5f5' : 'white',
                minHeight: '28px',
                marginTop: '2px'
              }}>
                <input
                  type="checkbox"
                  name="IsPrimary"
                  checked={formData.IsPrimary}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  style={{
                    width: '12px',
                    height: '12px',
                    accentColor: '#667eea'
                  }}
                />
                <Typography style={{
                  fontSize: '11px',
                  fontWeight: '500',
                  color: '#4a5568'
                }}>
                  Set as Primary Bank
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{ 
          padding: '8px 12px',
          background: '#f8f9fa',
          borderTop: '1px solid #e9ecef',
          gap: '6px',
          minHeight: 'auto'
        }}>
          <Button 
            onClick={onClose}
            style={{
              color: '#6c757d',
              textTransform: 'none',
              fontWeight: '500',
              fontSize: '12px',
              padding: '4px 8px'
            }}
          >
            Cancel
          </Button>
          {!isReadOnly && (
            <Button 
              onClick={handleSave}
              disabled={loading}
              variant="contained"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textTransform: 'none',
                fontWeight: '600',
                borderRadius: '4px',
                padding: '4px 12px',
                fontSize: '12px'
              }}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Add Bank' : 'Update Bank'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>

    {/* Primary Bank Warning Dialog */}
    <Dialog
      open={primaryWarningDialogOpen}
      onClose={() => setPrimaryWarningDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px'
        }
      }}
    >
      <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
        <WarningAmberIcon 
          style={{ 
            fontSize: '64px', 
            color: '#ff9800',
            marginBottom: '16px'
          }} 
        />
        <Typography 
          variant="h6" 
          style={{ 
            fontWeight: '600', 
            marginBottom: '12px',
            color: '#2d3748',
            fontSize: '16px'
          }}
        >
          Primary Bank Already Exists
        </Typography>
        <Typography 
          style={{ 
            color: '#666',
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >
          {primaryWarningMessage}
        </Typography>
      </DialogContent>
      <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
        <Button
          onClick={() => setPrimaryWarningDialogOpen(false)}
          variant="contained"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textTransform: 'none',
            fontWeight: '600',
            borderRadius: '8px',
            padding: '8px 32px',
            fontSize: '14px'
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  </>
  );
};

export default BankModal;